import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormArray,
} from '@angular/forms';

import { DietService } from 'src/app/services/diet.service';
import { StorageService } from 'src/app/services/storage.service';
import { ToastService } from 'src/app/services/toast.service';

import { Diet } from 'src/app/interfaces/diet';
import { User } from 'src/app/interfaces/user';
import { StateService } from 'src/app/services/state.service';
import * as dayjs from 'dayjs';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-diet-form-modal',
  templateUrl: './diet-form-modal.component.html',
  styleUrls: ['./diet-form-modal.component.scss'],
})
export class DietFormModalComponent implements OnInit {
  @ViewChild('dietFormModal') dietModal: ElementRef | undefined;

  @Input() diet: Diet | undefined;

  @Output() dietCreated = new EventEmitter<Diet>();
  @Output() dietUpdated = new EventEmitter<Diet>();

  numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  editing: boolean = false;

  availableDaysOfWeek = [
    { value: 'MONDAY', label: 'Lunes' },
    { value: 'TUESDAY', label: 'Martes' },
    { value: 'WEDNESDAY', label: 'Miércoles' },
    { value: 'THURSDAY', label: 'Jueves' },
    { value: 'FRIDAY', label: 'Viernes' },
    { value: 'SATURDAY', label: 'Sábado' },
    { value: 'SUNDAY', label: 'Domingo' },
  ];

  user: User | undefined;
  selectedUser: User | undefined;

  dietForm: UntypedFormGroup;

  constructor(
    private fb: UntypedFormBuilder,
    private toastService: ToastService,
    private dietService: DietService,
    private storageService: StorageService,
    private stateService: StateService
  ) {
    this.dietForm = this.fb.group({
      name: ['', [Validators.required]],
      description: ['', [Validators.required]],
      totalWeeks: [null, [Validators.required]],
      water: [null, []],
      allowCheatMeals: [null, [Validators.required]],
      periodCheatMeals: ['7', [Validators.required]],
      daysOfWeek: [[], [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.user = this.storageService.get('staffUser');
    this.selectedUser = this.storageService.get('selectedUser');

    this.stateService.edition$.subscribe({
      next: (editing: boolean) => {
        this.editing = editing;

        if (editing) {
          const finishAt = dayjs(this.diet?.finishedAt).utc();
          const startAt = dayjs(this.diet?.startedAt).utc();

          const totalWeeks = finishAt.diff(startAt, 'weeks');

          this.name.setValue(this.diet!.name);
          this.description.setValue(this.diet!.description);
          this.totalWeeks.setValue(totalWeeks);
          this.periodCheatMeals.setValue(this.diet!.periodCheatMeals);
          this.allowCheatMeals.setValue(this.diet!.allowCheatMeals);
          this.daysOfWeek.setValue(this.diet!.daysOfWeek || []);
        }
      },
    });
  }

  revalidateForm(): boolean {
    let returnValue: boolean = true;

    Object.keys(this.dietForm.controls).forEach((field) => {
      if (!this.dietForm.controls[field].value) {
        this.toastService.alert('Debes completar todos los campos.');
        returnValue = false;
      }
    });

    return returnValue;
  }

  onSubmit() {
    this.editing ? this.updateDiet() : this.createDiet();
  }

  createDiet() {
    if (!this.revalidateForm()) {
      return;
    }

    const diet: Pick<
      Diet,
      | 'userId'
      | 'name'
      | 'description'
      | 'totalWeeks'
      | 'allowCheatMeals'
      | 'periodCheatMeals'
      | 'water'
      | 'daysOfWeek'
    > = {
      name: this.name.value,
      description: this.description.value,
      userId: this.selectedUser!.id,
      totalWeeks: this.totalWeeks.value,
      allowCheatMeals: parseInt(this.allowCheatMeals.value),
      periodCheatMeals: parseInt(this.periodCheatMeals.value),
      water: this.water.value,
      daysOfWeek: this.daysOfWeek.value,
    };

    this.dietService.createDiet(diet).subscribe({
      next: (diet: Diet) => {
        this.dietCreated.emit(diet);
        this.clearForm();
        this.toastService.success('Se creó la dieta con éxito.');
      },
      error: (_error) => {
        if (_error.status == 400) {
          this.toastService.alert(_error.error.messageError);
        } else {
          this.toastService.alert(
            'Ha ocurrido un error creando la dieta. Si el error persiste contactarse con el soporte.'
          );
        }
      },
    });
  }

  updateDiet() {
    this.diet!.description = this.description.value;
    this.diet!.name = this.name.value;
    this.diet!.totalWeeks = this.totalWeeks.value;
    this.diet!.allowCheatMeals = parseInt(this.allowCheatMeals.value);
    this.diet!.periodCheatMeals = parseInt(this.periodCheatMeals.value);
    this.diet!.water = this.water.value;
    this.diet!.daysOfWeek = this.daysOfWeek.value;

    this.dietService.updateDiet(this.diet!).subscribe({
      next: (diet: Diet) => {
        this.clearForm();
        this.toastService.success('Se editó la dieta con éxito.');
        this.dietUpdated.emit();
      },
      error: (response: HttpErrorResponse) => {
        this.toastService.alert(response.error.messageError);
      },
    });
  }

  clearForm() {
    this.name.setValue('');
    this.description.setValue('');
    this.daysOfWeek.setValue([]);
    this.dietForm.reset();
  }

  get name() {
    return this.dietForm.controls['name'];
  }

  get water() {
    return this.dietForm.controls['water'];
  }

  get description() {
    return this.dietForm.controls['description'];
  }

  get totalWeeks() {
    return this.dietForm.controls['totalWeeks'];
  }

  get allowCheatMeals() {
    return this.dietForm.controls['allowCheatMeals'];
  }

  get periodCheatMeals() {
    return this.dietForm.controls['periodCheatMeals'];
  }

  get daysOfWeek() {
    return this.dietForm.controls['daysOfWeek'];
  }

  isDaySelected(day: string): boolean {
    return this.daysOfWeek.value?.includes(day) || false;
  }

  onDayChange(event: Event, day: string): void {
    const checked = (event.target as HTMLInputElement).checked;
    const currentDays = this.daysOfWeek.value || [];

    if (checked && !currentDays.includes(day)) {
      this.daysOfWeek.setValue([...currentDays, day]);
    } else if (!checked && currentDays.includes(day)) {
      this.daysOfWeek.setValue(currentDays.filter((d: any) => d !== day));
    }

    // Mark as touched to trigger validation
    this.daysOfWeek.markAsTouched();
  }
}
